/**
 * Error Boundary Components
 *
 * React error boundaries for catching and gracefully handling runtime errors.
 * Three tiers:
 *   - AppErrorBoundary: Top-level, catches anything that escapes feature/renderer boundaries.
 *   - RendererErrorBoundary: Wraps PixiJS / canvas renderers that are prone to WebGL crashes.
 *   - FeatureErrorBoundary: Wraps socket-dependent or feature-specific page sections.
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Label shown in the fallback UI (e.g. "Battle Renderer", "Lobby") */
  label?: string;
  /** Optional callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// ---------------------------------------------------------------------------
// Inline styles (no external CSS dependency)
// ---------------------------------------------------------------------------

const containerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  textAlign: 'center',
  minHeight: '200px',
};

const headingStyle: React.CSSProperties = {
  fontSize: '1.25rem',
  fontWeight: 600,
  marginBottom: '0.75rem',
  color: '#e74c3c',
};

const messageStyle: React.CSSProperties = {
  fontSize: '0.9rem',
  color: '#aaa',
  marginBottom: '1rem',
  maxWidth: '480px',
  lineHeight: 1.5,
};

const buttonStyle: React.CSSProperties = {
  padding: '0.5rem 1.25rem',
  fontSize: '0.9rem',
  fontWeight: 600,
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  color: '#fff',
  background: '#3498db',
  marginRight: '0.5rem',
};

const secondaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  background: '#555',
};

const detailsStyle: React.CSSProperties = {
  marginTop: '1rem',
  fontSize: '0.75rem',
  color: '#666',
  maxWidth: '480px',
  wordBreak: 'break-word',
};

// ---------------------------------------------------------------------------
// AppErrorBoundary -- top-level, full-page fallback with reload
// ---------------------------------------------------------------------------

export class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('[AppErrorBoundary] Uncaught error:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div style={{ ...containerStyle, minHeight: '100vh', background: '#1a1a2e' }}>
          <h1 style={headingStyle}>Something went wrong</h1>
          <p style={messageStyle}>
            An unexpected error crashed the application.
            Click the button below to reload.
          </p>
          <div>
            <button style={buttonStyle} onClick={this.handleReload}>
              Reload Page
            </button>
          </div>
          {this.state.error && (
            <p style={detailsStyle}>{this.state.error.message}</p>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// RendererErrorBoundary -- for PixiJS / canvas renderer sections
// ---------------------------------------------------------------------------

export class RendererErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(`[RendererErrorBoundary:${this.props.label ?? 'unknown'}] Renderer error:`, error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      const label = this.props.label ?? 'Renderer';
      return (
        <div style={{ ...containerStyle, background: '#111', borderRadius: '8px' }}>
          <h2 style={{ ...headingStyle, fontSize: '1.1rem' }}>{label} crashed</h2>
          <p style={messageStyle}>
            The graphics renderer encountered an error. You can try reloading the component or refresh the page.
          </p>
          <div>
            <button style={buttonStyle} onClick={this.handleRetry}>
              Retry
            </button>
            <button style={secondaryButtonStyle} onClick={this.handleReload}>
              Reload Page
            </button>
          </div>
          {this.state.error && (
            <p style={detailsStyle}>{this.state.error.message}</p>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

// ---------------------------------------------------------------------------
// FeatureErrorBoundary -- for socket-dependent / feature sections
// ---------------------------------------------------------------------------

export class FeatureErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(`[FeatureErrorBoundary:${this.props.label ?? 'unknown'}] Feature error:`, error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = (): void => {
    window.location.href = '/home';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      const label = this.props.label ?? 'This feature';
      return (
        <div style={containerStyle}>
          <h2 style={{ ...headingStyle, fontSize: '1.1rem' }}>{label} hit an error</h2>
          <p style={messageStyle}>
            Something went wrong loading this section.
            You can retry or head back to the home page.
          </p>
          <div>
            <button style={buttonStyle} onClick={this.handleRetry}>
              Retry
            </button>
            <button style={secondaryButtonStyle} onClick={this.handleGoHome}>
              Go Home
            </button>
          </div>
          {this.state.error && (
            <p style={detailsStyle}>{this.state.error.message}</p>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
